import { useState, useEffect, useRef } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import {
  Wallet,
  Loader2,
  Check,
  Banknote,
  Smartphone,
  CreditCard,
  Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface QuickExpenseModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

const expenseCategories = [
  { value: "Rent", label: "🏠 Rent" },
  { value: "Electricity", label: "⚡ Electricity" },
  { value: "Internet", label: "🌐 Internet" },
  { value: "Salary", label: "💰 Salary" },
  { value: "Stationery", label: "📎 Stationery" },
  { value: "Maintenance", label: "🔧 Maintenance" },
  { value: "Travel", label: "🚗 Travel" },
  { value: "Food", label: "🍔 Food" },
  { value: "Other", label: "📦 Other" },
];

const paymentModes = [
  { id: "cash", label: "Cash", icon: Banknote, shortcut: "1" },
  { id: "upi", label: "UPI", icon: Smartphone, shortcut: "2" },
  { id: "card", label: "Card", icon: CreditCard, shortcut: "3" },
];

export function QuickExpenseModal({ open, onOpenChange, onSuccess }: QuickExpenseModalProps) {
  const isMobile = useIsMobile();
  const { toast } = useToast();
  const { user, tenant, refreshSession, setBillingInProgress, isSessionReady, ensureSession } = useAuth();
  const queryClient = useQueryClient();
  const amountInputRef = useRef<HTMLInputElement>(null);

  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMode, setPaymentMode] = useState("cash");
  const [description, setDescription] = useState("");
  const [expenseDate, setExpenseDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-focus amount input on open
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        amountInputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  // Keyboard shortcuts
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC to close
      if (e.key === "Escape") {
        e.preventDefault();
        handleClose();
        return;
      }

      // Ctrl+Enter to submit
      if (e.key === "Enter" && e.ctrlKey && category && amount) {
        e.preventDefault();
        handleSubmit();
        return;
      }

      // Alt+1/2/3 for payment modes
      if (e.altKey && ["1", "2", "3"].includes(e.key)) {
        e.preventDefault();
        const modeIndex = parseInt(e.key) - 1;
        if (paymentModes[modeIndex]) {
          setPaymentMode(paymentModes[modeIndex].id);
        }
        return;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, category, amount, isSubmitting]);

  const resetForm = () => {
    setCategory("");
    setAmount("");
    setPaymentMode("cash");
    setDescription("");
    setExpenseDate(format(new Date(), "yyyy-MM-dd"));
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleSubmit = async () => {
    // CRITICAL: Action-level safety check - be more descriptive
    if (!isSessionReady) {
      // Determine what's actually missing
      const userId = user?.id;
      const tenantId = tenant?.id;
      
      if (!userId) {
        toast({ title: "Session Error", description: "Not logged in. Please refresh the page.", variant: "destructive" });
        return;
      }
      if (!tenantId) {
        toast({ title: "Setup Required", description: "Your workspace is not configured. Please contact support.", variant: "destructive" });
        return;
      }
      
      // If we have user and tenant but session not "ready", still try to proceed
      console.log('[QuickExpenseModal] isSessionReady false but have user and tenant, proceeding...');
    }
    
    setBillingInProgress(true);
    
    // Safe access with null checks - get these early
    const userId = user?.id;
    const tenantId = tenant?.id;
    
    // Final validation before API call
    if (!userId) {
      toast({ title: "Session Error", description: "Please refresh and try again", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }
    
    if (!tenantId) {
      toast({ title: "Setup Required", description: "Workspace not configured", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    if (!category || !amount) {
      toast({ title: "Error", description: "Please fill category and amount", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      setBillingInProgress(false);
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase.from("expenses").insert({
        tenant_id: tenantId,
        user_id: userId,
        category,
        amount: amountNum,
        payment_mode: paymentMode,
        description: description.trim() || null,
        expense_date: expenseDate,
      });

      if (error) throw error;

      // Invalidate all relevant queries
      queryClient.invalidateQueries({ queryKey: ["expenses"] });
      queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["daily-expenses"] }); // For Daily Book integration
      queryClient.invalidateQueries({ queryKey: ["dashboard-stats"] }); // For Dashboard stats

      toast({ title: "✅ Expense Added!", description: `₹${amountNum.toLocaleString()} recorded` });
      handleClose();
      onSuccess();
    } catch (error: any) {
      console.error('[QuickExpenseModal] Error:', error);
      if (error.message?.includes('JWT') || error.code === 'PGRST301') {
        const refreshed = await refreshSession();
        if (refreshed) {
          toast({ title: "Session refreshed", description: "Please try again", variant: "default" });
        } else {
          toast({ title: "Session Expired", description: "Please login again", variant: "destructive" });
        }
      } else {
        toast({ title: "Error", description: error.message || "Failed to add expense", variant: "destructive" });
      }
    } finally {
      setIsSubmitting(false);
      setBillingInProgress(false);
    }
  };

  const quickAmounts = [100, 500, 1000, 5000];

  const content = (
    <div className="space-y-4 sm:space-y-6">
      {/* Amount - PRIMARY FOCUS */}
      <div className="space-y-2 sm:space-y-3">
        <Label className="text-sm sm:text-lg font-bold">Amount *</Label>
        <div className="relative">
          <span className="absolute left-4 sm:left-5 top-1/2 -translate-y-1/2 text-2xl sm:text-3xl text-muted-foreground font-bold">₹</span>
          <Input
            ref={amountInputRef}
            type="number"
            inputMode="numeric"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="h-16 sm:h-20 text-3xl sm:text-4xl font-black pl-12 sm:pl-14 text-center"
            autoComplete="off"
          />
        </div>
        <div className="flex gap-1.5 sm:gap-2">
          {quickAmounts.map((amt) => (
            <Button
              key={amt}
              variant="outline"
              className="flex-1 h-9 sm:h-12 text-xs sm:text-base font-semibold touch-manipulation"
              onClick={() => setAmount(String(parseFloat(amount || "0") + amt))}
            >
              +₹{amt}
            </Button>
          ))}
        </div>
      </div>

      {/* Category */}
      <div className="space-y-2 sm:space-y-3">
        <Label className="text-sm sm:text-lg font-bold">Category *</Label>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="h-12 sm:h-14 text-sm sm:text-lg">
            <SelectValue placeholder="Select category" />
          </SelectTrigger>
          <SelectContent>
            {expenseCategories.map((cat) => (
              <SelectItem key={cat.value} value={cat.value} className="text-sm sm:text-lg py-3 sm:py-4">
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Payment Mode */}
      <div className="space-y-2 sm:space-y-3">
        <Label className="text-sm sm:text-lg font-bold">Payment</Label>
        <div className="flex gap-1.5 sm:gap-3">
          {paymentModes.map((pm) => (
            <Button
              key={pm.id}
              variant={paymentMode === pm.id ? "default" : "outline"}
              className={cn(
                "flex-1 h-10 sm:h-14 gap-1 sm:gap-2 text-xs sm:text-base font-semibold transition-all touch-manipulation",
                paymentMode === pm.id && "ring-2 ring-primary/30"
              )}
              onClick={() => setPaymentMode(pm.id)}
            >
              <pm.icon className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>{pm.label}</span>
              <kbd className="hidden lg:inline-flex ml-auto px-1.5 py-0.5 text-[10px] bg-background/30 rounded">Alt+{pm.shortcut}</kbd>
            </Button>
          ))}
        </div>
      </div>

      {/* Date */}
      <div className="space-y-2 sm:space-y-3">
        <Label className="text-sm sm:text-lg font-bold flex items-center gap-1.5 sm:gap-2">
          <Calendar className="h-4 w-4 sm:h-5 sm:w-5" />
          Date
        </Label>
        <Input
          type="date"
          value={expenseDate}
          onChange={(e) => setExpenseDate(e.target.value)}
          className="h-11 sm:h-14 text-sm sm:text-lg"
        />
      </div>

      {/* Notes - Optional */}
      <div className="space-y-1.5 sm:space-y-2">
        <Label className="text-xs sm:text-base font-semibold text-muted-foreground">Note (Optional)</Label>
        <Textarea
          placeholder="Add a quick note..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="text-sm sm:text-base resize-none min-h-[50px] sm:min-h-[60px]"
        />
      </div>

      {/* Submit */}
      <div className="pt-1 sm:pt-2">
        <Button
          size="lg"
          className="w-full h-12 sm:h-16 text-base sm:text-xl font-bold gap-2 sm:gap-3 shadow-lg hover:shadow-xl transition-all touch-manipulation"
          onClick={handleSubmit}
          disabled={isSubmitting || !category || !amount}
        >
          {isSubmitting ? <Loader2 className="h-5 w-5 sm:h-6 sm:w-6 animate-spin" /> : <Check className="h-5 w-5 sm:h-6 sm:w-6" />}
          <span>Add Expense</span>
          <kbd className="hidden sm:inline-flex px-2 py-1 text-xs bg-background/30 rounded">Ctrl+↵</kbd>
        </Button>
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[92vh] p-3 pb-6">
          <DrawerHeader className="p-0 pb-3">
            <DrawerTitle className="text-xl sm:text-2xl font-black flex items-center gap-2">
              <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              Add Expense
            </DrawerTitle>
          </DrawerHeader>
          {content}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="fixed inset-2 sm:inset-auto sm:left-[50%] sm:top-[50%] sm:translate-x-[-50%] sm:translate-y-[-50%] w-[calc(100%-1rem)] sm:w-full sm:max-w-lg h-auto max-h-[calc(100%-1rem)] sm:max-h-[90vh] flex flex-col p-4 sm:p-6 overflow-hidden rounded-lg">
        <DialogHeader className="flex-shrink-0 pb-2 sm:pb-0">
          <DialogTitle className="text-xl sm:text-2xl font-black flex items-center gap-2">
            <Wallet className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            Add Expense
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto">
          {content}
        </div>
      </DialogContent>
    </Dialog>
  );
}
